import { Fragment, useCallback, useEffect, useState } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import { listVisitasDeRuta } from '../../api/ruta';
import type { VisitaEnLista } from '../../api/ruta';
import { INSTRUMENTOS } from '../../domain/ruta';
import {
  BotonDetalle, Dato, FILA_ABIERTA, PanelDeDetalle, Seccion, Senal, FotoPrivada,
} from '../../components/DetalleExpandible';
import BorrarRegistro from '../../components/BorrarRegistro';
import { useAuth } from '../../components/AuthProvider';
import { puedeEditar } from '../../domain/permissions';
import { borrarVisitaDeRuta } from '../../api/ruta';
import TarjetasResumen from '../../components/TarjetasResumen';

const TZ = 'America/Lima';
const hoyLima = (): string => formatInTimeZone(new Date(), TZ, 'yyyy-MM-dd');

const enLima = (iso: string): string => formatInTimeZone(new Date(iso), TZ, 'dd/MM/yyyy HH:mm');

/** Una cosa hecha en la parada, ya traducida a lo que el supervisor leyó en el celular. */
interface CosaHecha {
  clave: string;
  etiqueta: string;
  ayuda: string;
  /** La visita es la base de la ruta; los formularios son extras. Se ven distinto por eso. */
  esVisita: boolean;
}

/**
 * Qué pasó en esta parada, en orden: primero la visita, después los formularios.
 *
 * Antes era un solo valor (`instrumento`) y por eso la columna era un texto. Dejó de serlo con la
 * corrección del usuario del 2026-09-05: *«el supervisor siempre es parte de su visita el ir a
 * visitar a la impulsadora; las otras acciones… son ADICIONALES a la visita»*. Una parada puede
 * entonces traer las tres cosas a la vez, y la bandeja tiene que poder mostrarlas juntas.
 */
function loQueHizo(f: VisitaEnLista): CosaHecha[] {
  const cosas: CosaHecha[] = [];
  if (f.visitoImpulsadora) {
    // La visita no es un instrumento —no vive en `INSTRUMENTOS`— y se rotula acá.
    cosas.push({
      clave: 'visita',
      etiqueta: 'Visita',
      ayuda: 'Pasó a ver a la impulsadora en el punto de venta.',
      esVisita: true,
    });
  }
  for (const valor of f.instrumentos) {
    // Un valor que la base ya guarda y este navegador no conoce se muestra crudo: un formulario
    // nuevo con una pestaña vieja abierta tiene que verse mal, no desaparecer de la fila.
    const opcion = INSTRUMENTOS.find((i) => i.valor === valor);
    cosas.push({
      clave: valor,
      etiqueta: opcion?.etiqueta ?? valor,
      ayuda: opcion?.ayuda ?? '',
      esVisita: false,
    });
  }
  return cosas;
}

/** Una bandera de la fila: el rótulo corto que va en la tabla y qué quiere decir. */
interface Bandera {
  clave: string;
  texto: string;
  ayuda: string;
}

/**
 * Las banderas de una visita, calculadas en UN solo sitio.
 *
 * La columna de la tabla y la sección del panel muestran exactamente lo mismo, y esta lista es lo
 * que impide que se separen: mientras eran dos bloques de JSX distintos, agregar una bandera
 * significaba acordarse de tocar los dos.
 *
 * La `ayuda` existe por el pedido (c) del usuario —*«otra persona que lo use no lo va a entender a
 * simple vista»*—: «sin GPS» no dice nada solo. Ninguna es un error; por eso todas van en ámbar.
 */
function banderasDe(f: VisitaEnLista): Bandera[] {
  const b: Bandera[] = [];
  if (f.registroTardio) {
    b.push({
      clave: 'tardio', texto: 'tardío',
      ayuda: 'Se cargó después del día que declara. Compará las dos horas de «Cuándo».',
    });
  }
  if (f.sinGps) {
    b.push({
      clave: 'sinGps', texto: 'sin GPS',
      ayuda: 'El celular no entregó ubicación al registrar, así que no se puede ubicar la parada.',
    });
  }
  if (f.tiendaFueraDeMaestra) {
    b.push({
      clave: 'tiendaNueva', texto: 'tienda nueva',
      ayuda: 'La tienda se escribió a mano: no está en la maestra de puntos de venta.',
    });
  }
  if (f.fotoPath === null) {
    b.push({
      clave: 'sinFoto', texto: 'sin foto',
      ayuda: 'La visita quedó sin constancia visual. La foto no es obligatoria.',
    });
  }
  return b;
}

/**
 * La foto de la visita es `FotoPrivada`, de `components/DetalleExpandible`.
 *
 * Vivia aca como componente local y se movio a compartido el 15-set, cuando la bandeja del
 * levantamiento de P&G necesito exactamente lo mismo: firmar al abrir, distinguir «sin foto» de
 * «no se pudo cargar», y mostrarla grande. Copiarla habria sido la cuarta copia de un patron en
 * este repo, y las copias se desincronizan — ya paso con el diccionario de banderas en tres
 * pantallas y con las tarjetas de indicador en dos tipografias distintas.
 */
const FotoDeVisita = ({ path }: { path: string }) => (
  <FotoPrivada path={path} alt="Foto de la visita" />
);

/**
 * Bandeja de VISITAS DE RUTA — lo que hace el supervisor de campo.
 *
 * Vive como solapa al lado de la bandeja de cierres de turno, dentro de «Registros de campo», y por
 * eso se ve igual que ella: los mismos filtros arriba, las mismas tres tarjetas, la misma tabla con
 * fila que se abre. El equipo mira las dos en la misma reunión y no debería tener que reaprender
 * dónde está cada cosa al cambiar de solapa.
 *
 * El supervisor NO marca asistencia (decisión del usuario, 2026-09-05): acá no hay nada que validar
 * contra un turno. Lo que se mira es otra cosa: si pasó, dónde pasó, y si dejó constancia.
 *
 * **No se filtra por persona.** La RLS ya decide qué filas llegan —el supervisor ve las suyas, la
 * oficina las de todos—, y un filtro del cliente encima de eso solo agregaría una promesa que no
 * sostiene nada.
 */
export default function RutaVisitasBandeja() {
  const { roles } = useAuth();
  const puedeBorrar = puedeEditar('canjes', roles);
  const [desde, setDesde] = useState(hoyLima());
  const [hasta, setHasta] = useState(hoyLima());
  const [filas, setFilas] = useState<VisitaEnLista[]>([]);
  const [abierta, setAbierta] = useState<string | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');

  const cargar = useCallback(async () => {
    setCargando(true);
    setError('');
    try {
      setFilas(await listVisitasDeRuta(desde, hasta));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudieron cargar las visitas.');
    } finally {
      setCargando(false);
    }
  }, [desde, hasta]);

  useEffect(() => { void cargar(); }, [cargar]);

  // Supervisores y tiendas se cuentan por NOMBRE y no por id: la tienda que no está en la maestra
  // no tiene id, y contando por id todas las tiendas nuevas del día colapsarían en una sola.
  const supervisores = new Set(filas.map((f) => f.supervisor)).size;
  const tiendas = new Set(filas.map((f) => f.tienda)).size;

  return (
    <div>
      {/* Sin filtro de campaña, y no es un olvido: una ruta cruza campañas en el mismo día —el
          supervisor pasa por la góndola de P&G y por la bodega de la esquina—, así que filtrar por
          campaña escondería media ruta y haría ver como floja una jornada completa. */}
      <div className="mb-4 flex flex-wrap items-end gap-2">
        <div>
          <label className="mb-1 block text-xs text-gray-600" htmlFor="rvb-desde">Desde</label>
          <input id="rvb-desde" type="date" value={desde}
            onChange={(e) => setDesde(e.target.value)}
            className="rounded border border-gray-300 px-2 py-1 text-sm" />
        </div>
        <div>
          <label className="mb-1 block text-xs text-gray-600" htmlFor="rvb-hasta">Hasta</label>
          <input id="rvb-hasta" type="date" value={hasta}
            onChange={(e) => setHasta(e.target.value)}
            className="rounded border border-gray-300 px-2 py-1 text-sm" />
        </div>
        <button type="button" onClick={() => void cargar()}
          className="rounded border border-gray-300 px-3 py-1 text-sm">
          Actualizar
        </button>
      </div>

      {/* Cuántas visitas hubo, cuánta gente salió a hacerlas y cuánto terreno cubrieron. Los tres
          primeros contestan lo que el usuario quiere saber de un vistazo: si la ruta se recorrió.

          Los otros dos son de calidad del dato, y por eso van con tono de atención: una visita sin
          GPS no se puede ubicar —no prueba que el supervisor estuvo ahí— y una tienda fuera de la
          maestra es una tienda que el catálogo no conoce, así que su visita no se puede cruzar con
          la programación. Contarlas es lo que hace que alguien las corrija. */}
      <TarjetasResumen indicadores={[
        { titulo: 'Visitas', valor: filas.length },
        { titulo: 'Supervisores', valor: supervisores },
        { titulo: 'Tiendas visitadas', valor: tiendas },
        { titulo: 'Sin GPS', valor: filas.filter((f) => f.sinGps).length, tono: 'atencion',
          detalle: 'no se puede ubicar la visita' },
        { titulo: 'Fuera de la maestra',
          valor: filas.filter((f) => f.tiendaFueraDeMaestra).length, tono: 'atencion',
          detalle: 'la tienda no está en el catálogo' },
      ]} />

      {error && <p className="mb-3 rounded bg-red-50 p-2 text-sm text-red-700">{error}</p>}
      {cargando && <p className="text-sm text-gray-500">Cargando…</p>}
      {!cargando && filas.length === 0 && (
        <p className="rounded border border-gray-200 bg-white p-4 text-sm text-gray-600">
          Todavía no hay visitas de ruta en ese rango. Acá cae cada parada del supervisor: a qué
          tienda llegó, a qué hora, qué levantó y la foto que dejó de constancia.
        </p>
      )}

      {filas.length > 0 && (
        // Mismo chrome que la bandeja de canjes y la de cierres: contenedor con borde, encabezado
        // gris y `px-3 py-2` en cada celda. Tres tablas que se miran en la misma reunión no pueden
        // tener tres densidades distintas.
        <div className="overflow-x-auto rounded border">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-600">
              <tr>
                <th className="px-3 py-2">Fecha</th>
                <th className="px-3 py-2">Supervisor</th>
                <th className="px-3 py-2">Tienda</th>
                <th className="px-3 py-2">Qué levantó</th>
                <th className="px-3 py-2">Señales</th>
                <th className="px-3 py-2"><span className="sr-only">Detalle</span></th>
              </tr>
            </thead>
            <tbody>
              {filas.map((f) => {
                const hecho = loQueHizo(f);
                const banderas = banderasDe(f);
                const abierto = abierta === f.id;
                return (
                  <Fragment key={f.id}>
                    {/* El separador va arriba de cada fila de datos y NO en el `<tbody>` con
                        `divide-y`: una línea entre la fila abierta y su panel los leería como dos
                        cosas sueltas, que es exactamente lo contrario de lo que el panel dice. */}
                    <tr className={`border-t border-gray-100 align-top ${abierto ? FILA_ABIERTA : ''}`}>
                      <td className="whitespace-nowrap px-3 py-2">{f.fecha}</td>
                      <td className="px-3 py-2">{f.supervisor}</td>
                      <td className="px-3 py-2">{f.tienda}</td>
                      {/* Pastillas y no una frase: pueden ser tres, y una frase larga en una celda
                          angosta obliga a leerla entera para saber si hubo levantamiento. No usan
                          `Senal` a propósito: no son banderas sino categorías, y la visita se
                          distingue del formulario por el color. */}
                      <td className="px-3 py-2">
                        {hecho.length === 0 ? '—' : (
                          <div className="flex flex-wrap gap-1">
                            {hecho.map((c) => (
                              <span key={c.clave}
                                className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs ${
                                  c.esVisita
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-gray-100 text-gray-800'}`}>
                                {c.etiqueta}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {banderas.length === 0 ? (
                          <span className="text-gray-500">—</span>
                        ) : (
                          <div className="flex flex-col items-start gap-1">
                            {banderas.map((b) => (
                              <Senal key={b.clave}>{b.texto}</Senal>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2">
                        {/* La RPC NIEGA el borrado si la visita tiene fichas de levantamiento
                            colgando, y muestra su mensaje tal cual: el cuestionario de campo es
                            una hora de trabajo y no es lo que el supervisor se equivoco al
                            registrar. */}
                        {/* Quien solo VE Registros de campo (el supervisor de ruta, 0218) no borra:
                            mostrarle el botón sería ofrecerle un rechazo del servidor. */}
                        {puedeBorrar && (
                          <span className="mr-1 inline-block">
                            <BorrarRegistro
                              sujeto={`Visita de ${f.supervisor} · ${f.tienda} · ${f.fecha}`}
                              onBorrar={(motivo) => borrarVisitaDeRuta(f.id, motivo)}
                              onBorrado={() => void cargar()}
                            />
                          </span>
                        )}
                        <BotonDetalle abierto={abierto}
                          onClick={() => setAbierta(abierto ? null : f.id)} />
                      </td>
                    </tr>
                    {abierto && (
                      <tr>
                        {/* `p-0`: el canal gris del panel tiene que llegar a los dos bordes de la
                            tabla. Es la superficie que rompe el empate con el fondo de la página,
                            y con el `px-3` de las demás celdas quedaría un marco blanco alrededor
                            que la anula. */}
                        <td colSpan={6} className="p-0">
                          <PanelDeDetalle
                            titulo="Detalle de la visita"
                            sujeto={`${f.tienda} · ${f.supervisor} · ${f.fecha}`}
                            onCerrar={() => setAbierta(null)}
                          >
                            {/* La rejilla la pone quien llama: `Seccion` con `ancho` se apoya en
                                ella para ocupar las dos columnas. */}
                            <div className="grid gap-x-8 gap-y-5 md:grid-cols-2">
                              {/* Va primero porque es la pregunta de la fila, y acá sí entra la
                                  ayuda de cada formulario —lo que dice QUÉ se levantó—. En la
                                  columna solo cabe el rótulo. */}
                              <Seccion titulo="Qué levantó" ancho>
                                {hecho.length === 0 ? (
                                  <p className="text-sm text-gray-600">
                                    No quedó registrado qué se hizo en esta parada.
                                  </p>
                                ) : (
                                  <ul className="space-y-2">
                                    {hecho.map((c) => (
                                      <li key={c.clave}>
                                        <span className="text-sm font-medium text-gray-900">
                                          {c.etiqueta}
                                        </span>
                                        {c.ayuda !== '' && (
                                          <span className="block text-sm text-gray-600">
                                            {c.ayuda}
                                          </span>
                                        )}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </Seccion>

                              <Seccion titulo="Dónde y con quién">
                                <dl className="space-y-2">
                                  <Dato etiqueta="Tienda">{f.tienda}</Dato>
                                  {/* La visita puede no colgar de ninguna campaña: el levantamiento
                                      por iniciativa propia, para una propuesta de negocio, está
                                      previsto. */}
                                  <Dato etiqueta="Campaña">{f.campania ?? 'Sin campaña'}</Dato>
                                  <Dato etiqueta="Impulsadora">
                                    {f.impulsadora ?? 'No se registró a quién fue a ver'}
                                  </Dato>
                                </dl>
                              </Seccion>

                              {/* Las dos horas van juntas y separadas: la distancia entre ellas es
                                  lo que respalda —o desmiente— la bandera de «tardío». */}
                              <Seccion titulo="Cuándo">
                                <dl className="space-y-2">
                                  <Dato etiqueta="Ocurrió">
                                    {f.ocurrioEn === null
                                      ? 'No quedó registrado (se cargó de memoria)'
                                      : enLima(f.ocurrioEn)}
                                  </Dato>
                                  <Dato etiqueta="Se registró">{enLima(f.registradoEn)}</Dato>
                                </dl>
                              </Seccion>

                              {/* Las mismas banderas de la columna, pero explicadas. En la tabla
                                  son dos palabras; acá es donde alguien que entra por primera vez
                                  averigua qué quieren decir. */}
                              <Seccion titulo="Señales" ancho>
                                {banderas.length === 0 ? (
                                  <p className="text-sm text-gray-600">
                                    Sin señales: se registró el mismo día, con ubicación, en una
                                    tienda de la maestra y con foto.
                                  </p>
                                ) : (
                                  <ul className="space-y-2">
                                    {banderas.map((b) => (
                                      <li key={b.clave}
                                        className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                                        <Senal>{b.texto}</Senal>
                                        <span className="text-sm text-gray-600">{b.ayuda}</span>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </Seccion>

                              <Seccion titulo="Notas" ancho>
                                {(f.notas ?? '').trim() === '' ? (
                                  <p className="text-sm text-gray-600">
                                    No dejó notas de esta parada.
                                  </p>
                                ) : (
                                  <p className="whitespace-pre-wrap rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900">
                                    {f.notas}
                                  </p>
                                )}
                              </Seccion>

                              {/* La foto va última y con su propia sección: es lo que más pesa de
                                  este detalle y necesita ancho, no un hueco entre dos campos. */}
                              <Seccion titulo="Foto de la visita" ancho>
                                {f.fotoPath === null ? (
                                  <p className="text-sm text-gray-600">
                                    No dejó foto de esta visita. No es obligatoria: hay tiendas
                                    donde no dejan sacar el celular.
                                  </p>
                                ) : (
                                  <FotoDeVisita path={f.fotoPath} />
                                )}
                              </Seccion>
                            </div>
                          </PanelDeDetalle>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
